import type { ApiResponse, Scanner, ScannerType } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';

export type CreateScannerPayload = {
  name: string;
  deviceId: string;
  type: ScannerType;
  status?: 'active' | 'inactive' | 'maintenance' | 'charging';
  branchId: string;
  shelfId?: string;
  assignedUserId?: string;
  tenantId?: string;
  deviceModelId?: string;
};

export type UpdateScannerPayload = {
  name?: string;
  deviceId?: string;
  type?: ScannerType;
  status?: 'active' | 'inactive' | 'maintenance' | 'charging';
  branchId?: string;
  shelfId?: string;
  assignedUserId?: string;
  tenantId?: string;
  deviceModelId?: string | null;
};

export interface ListScannersPaginatedParams {
  page: number;
  limit: number;
}

export interface ListScannersPaginatedResult {
  data: Scanner[];
  total: number;
  page: number;
  limit: number;
}

export async function listScannersPaginated(
  params: ListScannersPaginatedParams,
): Promise<ListScannersPaginatedResult> {
  const { page, limit } = params;
  const response = await apiClient.get<ApiResponse<ListScannersPaginatedResult>>(
    '/api/scanners',
    { params: { page, limit } },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function listScanners(): Promise<Scanner[]> {
  const response = await apiClient.get<ApiResponse<Scanner[]>>('/api/scanners');
  return response.data.data;
}

export async function getScannerById(id: string): Promise<Scanner> {
  const response = await apiClient.get<ApiResponse<Scanner>>(`/api/scanners/${id}`);
  return response.data.data;
}

export async function createScanner(payload: CreateScannerPayload): Promise<Scanner> {
  const response = await apiClient.post<ApiResponse<Scanner>>('/api/scanners', payload);
  return response.data.data;
}

export async function updateScanner(
  id: string,
  payload: UpdateScannerPayload,
): Promise<Scanner> {
  const response = await apiClient.put<ApiResponse<Scanner>>(
    `/api/scanners/${id}`,
    payload,
  );
  return response.data.data;
}

export async function deleteScanner(id: string): Promise<void> {
  await apiClient.delete<ApiResponse<void>>(`/api/scanners/${id}`);
}
